import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Button, Loading, Modal } from '../../components/ui.js';
import { api } from '../../lib/api.js';
import { money } from '../../lib/money.js';
import { sizeGrids } from '../purchasing/size-grid.js';
import { TOP, brandStep, browseModel } from './browse-categories.js';
import type { BrowseAt } from './browse-categories.js';

/**
 * A style's sizes as a grid at the register (2026-10-01, decision 182).
 *
 * The client: "give them the grid form and department drill-down with or
 * after the search, like you would on an Android touch screen — a grid view of
 * the variant in the search dropdown would be easier, with expand into the
 * grid that we use in the inventory and receiving area." Search lists sizes
 * one per row, fifty at most; a style with sixty is a scroll and a guess.
 * Here a style is the grid the style page and receiving use — colors down,
 * sizes across, one grid per length — and a tap on a size rings it up.
 *
 * A size with none available still rings: the register sells what is in the
 * customer's hand and says so in the cart (decision 172).
 */

export function StyleGridPicker({
  productId,
  onPick,
  onBack,
  onClose,
}: {
  productId: string;
  onPick: (variantId: string) => void;
  /** Back to the list this came from, when it came from Browse. */
  onBack?: () => void;
  onClose: () => void;
}) {
  const style = useQuery({
    queryKey: ['products', 'detail', productId],
    queryFn: () => api.products.get.query({ productId }),
    staleTime: 0,
  });

  const product = style.data?.product;
  const variants = (style.data?.variants ?? []).filter((variant) => variant.active);
  const prices = variants.map((variant) => variant.retailCents);
  const low = prices.length === 0 ? 0 : Math.min(...prices);
  const high = prices.length === 0 ? 0 : Math.max(...prices);

  return (
    <Modal
      open
      wide
      title={product?.name ?? 'Loading the model'}
      description={
        product === undefined
          ? undefined
          : `${product.styleNumber} · ${low === high ? money(low) : `${money(low)} – ${money(high)}`} · tap a size to ring it up`
      }
      onClose={onClose}
      secondaryAction={
        onBack === undefined ? (
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        ) : (
          <Button variant="ghost" onClick={onBack}>
            Back
          </Button>
        )
      }
    >
      {style.isPending ? (
        <Loading label="Loading the model" />
      ) : (
        sizeGrids(variants).map((grid) => (
          <div className="table-scroll" key={grid.altSize ?? 'one'}>
            <table className="table matrix pick-grid">
              {grid.label !== null ? (
                <caption className="matrix__caption">{grid.label}</caption>
              ) : null}
              <thead>
                <tr>
                  <th />
                  {grid.sizes.map((size, index) => (
                    <th key={size ?? `size-${index}`} className="num">
                      {size ?? 'One size'}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {grid.rows.map((row, rowIndex) => (
                  <tr key={row.key}>
                    <th scope="row" className="matrix__row-head">
                      {row.label}
                    </th>
                    {row.cells.map((variant, index) => {
                      if (variant === undefined) return <td key={index} />;
                      const available = variant.stock.onHand - variant.stock.reserved;
                      const label = [row.colorName, variant.sizeLabel, variant.altSize]
                        .filter((part) => part !== null && part !== '')
                        .join(' ');
                      return (
                        <td key={variant.id} className="num">
                          <button
                            type="button"
                            className={`pick-grid__cell${available > 0 ? '' : ' pick-grid__cell--out'}`}
                            aria-label={`Ring up ${label}, ${available} available`}
                            onClick={() => onPick(variant.id)}
                          >
                            <span className="pick-grid__count">{available}</span>
                            {variant.retailCents !== low || low !== high ? (
                              <span className="pick-grid__price money">
                                {money(variant.retailCents)}
                              </span>
                            ) : null}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}
    </Modal>
  );
}

/**
 * Category, then subcategory, then style, then the grid — the walk a touch till
 * offers when nobody remembers the style number (decisions 182, 214). The
 * rules of the walk are `browse-categories.ts`'s; this draws them.
 */
export function BrowseDialog({
  onPick,
  onClose,
}: {
  onPick: (variantId: string) => void;
  onClose: () => void;
}) {
  const [path, setPath] = useState<BrowseAt[]>([]);
  const [productId, setProductId] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [brand, setBrand] = useState<string | null>(null);

  const taxonomy = useQuery({
    queryKey: ['products', 'taxonomy'],
    queryFn: () => api.products.taxonomy.query(),
    staleTime: 300_000,
  });
  const model = useMemo(
    () =>
      taxonomy.data === undefined
        ? null
        : browseModel(
            taxonomy.data.departments.map((node) => ({
              id: node.id,
              name: node.name,
              parentId: node.parentId,
              path: node.path,
            })),
            taxonomy.data.filed,
            taxonomy.data.genders,
            taxonomy.data.groups,
          ),
    [taxonomy.data],
  );

  const at = path[path.length - 1] ?? TOP;
  const view = model?.view(at) ?? null;
  const list = view?.list ?? null;
  const styles = useQuery({
    queryKey: ['products', 'list', 'browse', list?.departmentId, list?.genderIds, filter.trim()],
    queryFn: () =>
      api.products.list.query({
        term: filter.trim(),
        departmentId: list!.departmentId,
        ...(list!.genderIds === null ? {} : { categoryIds: [...list!.genderIds] }),
        limit: 200,
      }),
    enabled: list !== null,
  });

  if (productId !== null) {
    return (
      <StyleGridPicker
        productId={productId}
        onPick={onPick}
        onBack={() => setProductId(null)}
        onClose={onClose}
      />
    );
  }

  const go = (next: BrowseAt) => {
    setPath([...path, next]);
    setFilter('');
    setBrand(null);
  };
  const back = () => {
    if (brand !== null) {
      setBrand(null);
      return;
    }
    setPath(path.slice(0, -1));
    setFilter('');
  };

  const wanted = filter.trim().toLowerCase();
  const tiles = view?.tiles ?? [];
  /*
   * Typed at the top, the box finds a category at any depth — search as you
   * type, recognition over recall — rather than only the tiles on screen.
   */
  const shownTiles =
    wanted === ''
      ? tiles
      : [
          ...tiles.filter((tile) => tile.label.toLowerCase().includes(wanted)),
          ...(at.nodeId === null
            ? (model?.everywhere ?? [])
                .filter((entry) => entry.label.toLowerCase().includes(wanted))
                .map((entry) => ({ kind: 'category' as const, label: entry.label, at: entry.at }))
            : []),
        ];
  const allStyles = styles.data ?? [];
  // A long list asks for the brand first (2026-10-15); typing skips it.
  const brands = brand === null && wanted === '' ? brandStep(allStyles) : null;
  const shownStyles =
    brand === null ? allStyles : allStyles.filter((style) => (style.brand ?? 'No brand') === brand);
  const title =
    view === null ? 'Browse by category' : brand === null ? view.title : `${view.title} › ${brand}`;

  return (
    <Modal
      open
      wide
      title={title}
      description={
        list !== null
          ? brands !== null
            ? 'Pick a brand, or type to find a model.'
            : 'Pick a model.'
          : at.nodeId === null
            ? 'Pick a category, then narrow it down to a model.'
            : 'Pick a group or a subcategory.'
      }
      onClose={onClose}
      secondaryAction={
        path.length === 0 ? (
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        ) : (
          <Button variant="ghost" onClick={back}>
            ← Back
          </Button>
        )
      }
    >
      {list === null ? (
        taxonomy.isPending ? (
          <Loading label="Loading categories" />
        ) : (
          <>
            {/* Narrowing helps where there is a lot to read, and at the top it finds any category. */}
            {tiles.length > 7 || at.nodeId === null ? (
              <input
                className="input"
                placeholder={
                  at.nodeId === null ? 'Type a category to go straight to it…' : 'Narrow the list…'
                }
                aria-label="Narrow the list"
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
              />
            ) : null}
            <div className="browse__tiles">
              {shownTiles.map((tile) => (
                <button
                  key={`${tile.kind}-${tile.label}-${tile.at.nodeId ?? ''}-${tile.at.group ?? ''}`}
                  type="button"
                  className={`browse__tile${tile.kind === 'category' ? '' : ' browse__tile--group'}`}
                  onClick={() => go(tile.at)}
                >
                  {tile.label}
                </button>
              ))}
            </div>
          </>
        )
      ) : (
        <>
          <input
            className="input"
            placeholder="Narrow the models…"
            aria-label="Narrow the models"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
          />
          {styles.isPending ? (
            <Loading label="Loading models" />
          ) : brands !== null ? (
            <div className="browse__tiles">
              {brands.map((entry) => (
                <button
                  key={entry.brand}
                  type="button"
                  className="browse__tile browse__tile--group"
                  onClick={() => setBrand(entry.brand)}
                >
                  <span>{entry.brand}</span>
                  <span className="text-secondary">
                    {entry.count} {entry.count === 1 ? 'model' : 'models'}
                  </span>
                </button>
              ))}
            </div>
          ) : shownStyles.length === 0 ? (
            <p className="text-secondary">Nothing in {title} matches.</p>
          ) : (
            <div className="browse__tiles">
              {shownStyles.map((style) => (
                <button
                  key={style.id}
                  type="button"
                  className="browse__tile"
                  onClick={() => setProductId(style.id)}
                >
                  <span>{style.name}</span>
                  <span className="text-secondary">
                    {style.styleNumber}
                    {style.trackInventory ? ` · ${style.onHand} on hand` : ''}
                  </span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
